/**
 * WALL NICHE (2026-10-07) — the ONE place a niche's built shape is decided, from its
 * own params and its host wall's assembly. resolveElement stamps the result onto the
 * resolved niche (`nicheSpec`), so the wall generator (the recess it cuts), the niche's
 * own geometry (lining, shelves, trim) and the palette (the limit it explains) all read
 * the same numbers.
 *
 * THE DEPTH RULE (Matt: "it should never be deeper than the wall allows for … not
 * allowing the clash and explaining to the user what is stopping it"): the finish
 * build-up on the niche's face wraps into the niche on all five sides, so the deepest
 * CLEAR depth is the wall's structural core (a 2x4 wall: 3-1/2"). A deeper typed depth
 * never builds; it is held at the limit and `depthLimitReason` says why.
 */
import type { ResolvedElement } from './params'
import type { AssemblyRecord, ParamValue } from './types'
import { parseLength } from './units'

export type NicheFace = 'exterior' | 'interior'
export type NicheEdge = 'metal' | 'mitered' | 'stone'

export interface NicheSpec {
  face: NicheFace
  /** physical side of the wall CENTERLINE the niche opens to: +1 = the wall path's
   *  left normal, -1 = its right */
  sign: 1 | -1
  /** the finish build-up on that face (what wraps into the niche), ft */
  liningFt: number
  /** the wall's structural core thickness, ft */
  coreFt: number
  /** deepest clear depth this wall allows, ft */
  maxDepthFt: number
  /** the clear depth that builds (typed depth held at the limit), ft */
  depthFt: number
  /** set when the typed depth was more than the wall allows */
  depthLimitReason: string | null
  /** clear (finished) width / height, after the tile snap */
  clearWFt: number
  clearHFt: number
  /** true when the tile snap changed the typed size */
  snapped: boolean
  tile: { wFt: number; hFt: number; groutFt: number } | null
  /** rough opening cut in the wall */
  roughWFt: number
  roughHFt: number
  roughDepthFt: number
  /** clear sill height above the niche's level (head mode resolved), ft */
  sillFt: number
  slopedSill: boolean
  sillDropFt: number
  edge: NicheEdge
  stoneProjectFt: number
  /** each shelf's BOTTOM, ft above the clear sill */
  shelves: number[]
  shelfTFt: number
  dividers: number
  dividerTFt: number
  led: boolean
  /** material ids per face (already defaulted to the wall's own finish on that face) */
  mats: { back: string; left: string; right: string; top: string; sill: string; shelf: string; edge: string; led: string }
}

const num = (v: ParamValue | undefined, d: number): number =>
  typeof v === 'number' && isFinite(v) ? v : d

/** a lining is never thinner than this, even on a wall with no finish on that face */
export const NICHE_MIN_LINING_FT = 0.5 / 12
/** brushed aluminum edge trim + the warm-white LED strip (sceneStore pseudo materials) */
export const NICHE_TRIM_MATERIAL = '__nicheTrim'
export const NICHE_LED_MATERIAL = '__nicheLed'

/** the build-up on one face of a wall: thickness outside the core, the outermost layer's
 *  material, and the core thickness */
export function wallFaceBuildUp(assembly: AssemblyRecord | null | undefined, face: NicheFace):
  { sideFt: number; coreFt: number; faceMat: string } {
  const layers = assembly?.layers ?? []
  if (!layers.length) return { sideFt: 0, coreFt: 0, faceMat: '' }
  const n = layers.length
  const start = Math.max(0, Math.min(assembly!.coreBoundary?.start ?? 0, n - 1))
  const end = Math.max(start, Math.min(assembly!.coreBoundary?.end ?? n - 1, n - 1))
  let coreFt = 0
  for (let i = start; i <= end; i++) coreFt += layers[i].thicknessFt || 0
  let sideFt = 0
  if (face === 'exterior') for (let i = 0; i < start; i++) sideFt += layers[i].thicknessFt || 0
  else for (let i = end + 1; i < n; i++) sideFt += layers[i].thicknessFt || 0
  const faceMat = String((face === 'exterior' ? layers[0] : layers[n - 1]).materialId || '')
  return { sideFt, coreFt, faceMat }
}

/** the deepest clear niche a wall allows on one face, ft (0 = none) */
export function nicheMaxDepthFt(assembly: AssemblyRecord | null | undefined, face: NicheFace): number {
  const { sideFt, coreFt } = wallFaceBuildUp(assembly, face)
  const lining = Math.max(sideFt, NICHE_MIN_LINING_FT)
  return Math.max(0, sideFt + coreFt - lining)
}

/** the plain-language reason a depth is refused, or null when it fits */
export function nicheDepthRefusal(depthFt: number, maxFt: number, wall: ResolvedElement | null | undefined): string | null {
  if (depthFt <= maxFt + 1e-6) return null
  const name = wall?.type?.name ? `This wall (${wall.type.name})` : 'This wall'
  if (maxFt <= 1e-6) return `${name} has no stud space to recess into, so a niche can't be cut here.`
  return `${name} only has ${fmtIn(maxFt)} of stud depth behind its finish. A niche can't go deeper than the studs without cutting into the room on the other side.`
}

function fmtIn(ft: number): string {
  const inches = Math.round(ft * 12 * 16) / 16
  const whole = Math.floor(inches + 1e-9)
  const frac = Math.round((inches - whole) * 16)
  if (!frac) return `${whole}"`
  let n = frac, d = 16
  while (n % 2 === 0) { n /= 2; d /= 2 }
  return whole ? `${whole} ${n}/${d}"` : `${n}/${d}"`
}

/** snap a clear size to whole tiles: n tiles + the joints between them, n ≥ 1 */
export function snapToTiles(sizeFt: number, tileFt: number, groutFt: number): number {
  if (!(tileFt > 0)) return sizeFt
  const pitch = tileFt + Math.max(0, groutFt)
  const n = Math.max(1, Math.round((sizeFt + Math.max(0, groutFt)) / pitch))
  return n * pitch - Math.max(0, groutFt)
}

/** shelf bottoms above the clear sill — even spacing, or the typed list (bare numbers
 *  read as INCHES: niche shelves are an inches-scale call) */
export function nicheShelfBottoms(params: Record<string, ParamValue>, clearHFt: number, shelfTFt: number): number[] {
  const count = Math.max(0, Math.min(6, Math.round(num(params.shelfCount, 0))))
  if (!count) return []
  const lo = shelfTFt, hi = clearHFt - shelfTFt   // keep every shelf inside the niche
  if (params.shelfSpacing === 'custom' && typeof params.shelfHeights === 'string' && params.shelfHeights.trim()) {
    const out: number[] = []
    for (const raw of params.shelfHeights.split(/[,;]/)) {
      const t = raw.trim()
      if (!t) continue
      const v = /^[\d.\s/]+$/.test(t) ? parseLength(t + '"') : parseLength(t)
      if (v == null || !isFinite(v)) continue
      out.push(Math.max(lo, Math.min(hi, v)))
      if (out.length >= count) break
    }
    if (out.length) return out.sort((a, b) => a - b)
  }
  // even: the clear height split into count+1 equal openings
  const gap = (clearHFt - count * shelfTFt) / (count + 1)
  return Array.from({ length: count }, (_, i) => gap * (i + 1) + shelfTFt * i)
}

/**
 * Derive the niche's built shape. `wall` is the resolved HOST (null = unhosted — the
 * spec still resolves, against no wall, so the palette can read it).
 */
export function nicheSpec(niche: ResolvedElement, wall: ResolvedElement | null | undefined): NicheSpec {
  const p = niche.params
  const tp = niche.typeParams
  const face: NicheFace = p.face === 'exterior' ? 'exterior' : 'interior'
  const flipped = wall?.params.flipped === true
  // exterior = the left normal on an unflipped wall (locationLine.ts sidedness)
  const sign: 1 | -1 = (face === 'exterior') !== flipped ? 1 : -1
  const { sideFt, coreFt, faceMat } = wallFaceBuildUp(wall?.assembly, face)
  const liningFt = Math.max(sideFt, NICHE_MIN_LINING_FT)
  const maxDepthFt = wall ? Math.max(0, sideFt + coreFt - liningFt) : num(p.depthFt, 3.5 / 12)
  const typedDepth = num(p.depthFt, 3.5 / 12)
  const depthLimitReason = wall ? nicheDepthRefusal(typedDepth, maxDepthFt, wall) : null
  const depthFt = Math.max(0, Math.min(typedDepth, maxDepthFt))

  const shower = tp.shower !== false
  const tileOn = p.tileSnap === 'on' || (p.tileSnap !== 'off' && shower)
  const tile = tileOn
    ? { wFt: num(p.tileWidthFt, 3 / 12), hFt: num(p.tileHeightFt, 6 / 12), groutFt: num(p.groutFt, 1 / 192) }
    : null
  const typedW = num(p.widthFt, 14.5 / 12), typedH = num(p.heightFt, 2)
  const clearWFt = tile ? snapToTiles(typedW, tile.wFt, tile.groutFt) : typedW
  const clearHFt = tile ? snapToTiles(typedH, tile.hFt, tile.groutFt) : typedH
  const snapped = !!tile && (Math.abs(clearWFt - typedW) > 1e-6 || Math.abs(clearHFt - typedH) > 1e-6)

  // head mode: the clear head is the stored value, the sill derives (window precedent)
  const sillFt = p.sillMode === 'head'
    ? Math.max(0, num(p.headHeightFt, 5.5) - clearHFt)
    : Math.max(0, num(p.sillFt, 3.5))

  const shelfTFt = num(p.shelfThicknessFt, 0.75 / 12)
  const dividerTFt = num(p.dividerThicknessFt, 0.75 / 12)
  const edge: NicheEdge = p.edge === 'mitered' || p.edge === 'stone' ? p.edge : 'metal'

  const pick = (v: ParamValue | undefined, fallback: string) => (typeof v === 'string' && v ? v : fallback)
  const wallFace = faceMat || 'default'
  const sillMat = pick(p.finishSill, wallFace)
  return {
    face, sign, liningFt, coreFt, maxDepthFt, depthFt, depthLimitReason,
    clearWFt, clearHFt, snapped, tile,
    roughWFt: clearWFt + 2 * liningFt,
    roughHFt: clearHFt + 2 * liningFt,
    roughDepthFt: depthFt + liningFt,
    sillFt,
    slopedSill: p.sillSlope === 'on' || (p.sillSlope !== 'off' && shower),
    sillDropFt: num(p.sillDropFt, 1 / 48),
    edge,
    stoneProjectFt: edge === 'stone' ? num(p.stoneProjectFt, 0.5 / 12) : 0,
    shelves: nicheShelfBottoms(p, clearHFt, shelfTFt),
    shelfTFt,
    dividers: Math.max(0, Math.min(4, Math.round(num(p.dividerCount, 0)))),
    dividerTFt,
    led: p.ledStrip === true,
    mats: {
      back: pick(p.finishBack, wallFace),
      left: pick(p.finishLeft, wallFace),
      right: pick(p.finishRight, wallFace),
      top: pick(p.finishTop, wallFace),
      sill: sillMat,
      shelf: pick(p.finishShelf, sillMat),
      edge: pick(p.edgeMaterial, NICHE_TRIM_MATERIAL),
      led: NICHE_LED_MATERIAL,
    },
  }
}
